import { useEffect, useMemo, useState } from "react";
import TableUI from "../../../components/Basic/TableUI";
import Icon, { ICON_NAME } from "../../../components/Icon";
import Action from "../../../components/Menu/Action";
import { useFilter } from "../../../contexts/FilterContext";
import { openPopup2 } from "../../../contexts/PopupContext";
import { CombineService } from "../../../util/services/combine";
import { selectPagePermissions } from "../../../util/store/selectors";
import { useSelector } from "../../../util/store/store";
import { aMinutesMillisecond, COMBINE_TREATMENT_LEVEL, combineTreatmentLevel } from "../../../util/varibles/constants";
import { durationTxt, showErrorResponse } from "../../../util/varibles/global";
import { ICombineTreatment } from "../../../util/varibles/interface";
import { ACTION } from "../../../util/varibles/permission";
import styles from "../style.module.scss";
import TreatmentTaskForm from "./TreatmentTaskForm";

export default function TreatmentTasks() {
    const permission = useSelector(selectPagePermissions(ACTION.COMBINE_TREATMENT));
    const [{ loading, data }, setData] = useState<{ loading: boolean, data: ICombineTreatment[] }>({ loading: false, data: [] });
    const [search] = useFilter(state => state.search);

    useEffect(() => {
        setData(prev => ({ ...prev, loading: true }));
        CombineService.gets()
            .then(rs => {
                setData({ data: rs, loading: false });
            })
            .catch(error => {
                setData(prev => ({ ...prev, loading: false }))
                showErrorResponse("Get combine treatment failed", error);
            })

    }, [])

    const handleAdd = (data?: ICombineTreatment) => {
        let title, onOk, onSend, nameOk;
        if (!data) {
            title = 'New combined treatment';
            onOk = (value: ICombineTreatment) => setData(prev => ({ ...prev, data: [...prev.data, value] }));
            nameOk = 'Create';
            onSend = CombineService.create
        } else {
            title = 'Update combined treatment: ' + data.name;
            onOk = (value: ICombineTreatment) => setData(prev => ({ ...prev, data: prev.data.map(item => item.id === value.id ? value : item) }))
            nameOk = 'Save';
            onSend = CombineService.update
        }

        openPopup2(<TreatmentTaskForm {...{ data, nameOk, onSend, onOk, }} />, { modalProps: { title } })
    }

    const handleAfterDelete = (id: string) => {
        setData(prev => ({ ...prev, data: prev.data.filter(item => item.id !== id) }));
    }

    const columns = [
        {
            key: 'name',
            title: 'Combined treatment name',
            dataIndex: 'name',
        },
        {
            key: 'type',
            title: 'Combined treatment types',
            dataIndex: 'sub_types',
            render: (data: { treatment_type: number, name: string, duration?: number }[]) => {
                return <div className="flex flex-wrap gap-[5px]">
                    {data.map((item) => <div key={item.treatment_type} className={styles.treatmentType}>
                        {item.name} {item.duration ? `(${durationTxt(item.duration * aMinutesMillisecond).trim()})` : ""}
                    </div>)}
                </div>
            }
        },
        {
            key: 'type',
            title: 'Scope',
            dataIndex: 'level',
            render: (data: COMBINE_TREATMENT_LEVEL) => {
                const { name } = combineTreatmentLevel[data] || {};
                return name;
            }
        },
        {
            key: 'action',
            width: 60,
            title: ' ',
            render: (data: ICombineTreatment) => <div className="pl-1 py-1">
                <Action<ICombineTreatment>
                    data={data}
                    onEdit={permission.update ? () => handleAdd(data) : undefined}
                    onDelete={permission.delete ? CombineService.delete : undefined}
                    afterDelete={handleAfterDelete}
                />
            </div>
        }
    ]

    const source = useMemo(() => data.filter(({ name }) => name.toLowerCase().includes((search || "").toLowerCase())), [data, search]);
    if (!permission.view)
        return null

    return <div>
        <div className="flex justify-end h-[52px] items-center">
            {permission.create && <div className="flex gap-1 items-center" onClick={() => handleAdd()}>
                <Icon icon={ICON_NAME.ADD} bg="colorLink" />
                <span className="link">
                    Define new combine treatment
                </span>
            </div>}
        </div>
        <TableUI columns={columns} loading={loading} dataSource={source} />
    </div>
}